import {
  DELIVERY_FEE_LABEL,
  DELIVERY_FEE_HELP,
} from 'src/utils/deliveryFeeCopy'
import React, { useEffect, useState } from 'react'
import { Modal, Form, Input, Select, Collapse, TimePicker, Button } from 'antd'
import { UserAddOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import DeliveryDaysSelect from 'src/components/DeliveryDaysField'
import moment from 'moment'

const { Option } = Select
const { Panel } = Collapse

const looksLikeZeroPriceFile = name => {
  if (!name) return false
  const n = String(name).toLowerCase()
  return (
    n.includes('£0') ||
    n.includes('£ 0') ||
    n.includes('zero price') ||
    /(^|[^0-9])0(\s*price|\s*file)/i.test(name)
  )
}

const InviteCustomerModal = ({
  visible,
  onCancel,
  onSubmit,
  form,
  reviewValues = {},
  onReviewChange,
}) => {
  const history = useHistory()
  const account = useSelector(state => state.auth.account)
  const suppliers = account?.supplier || []
  const supplierDisplayName =
    account?.supplier?.[0]?.title || account?.supplierTitle || 'Your supplier'

  const priceFiles = suppliers.map(supplier => ({
    id: supplier._id,
    name: [supplier.title, supplier.secondTitle].filter(Boolean).join(' '),
  }))

  const [advancedExpanded, setAdvancedExpanded] = useState(false)
  const [sending, setSending] = useState(false)
  const { getFieldDecorator, getFieldValue } = form

  const selectedPriceFileId =
    getFieldValue('priceFile') || reviewValues.priceFile
  const selectedPriceFile = priceFiles.find(f => f.id === selectedPriceFileId)
  const hasZeroNamedFile = priceFiles.some(f => looksLikeZeroPriceFile(f.name))

  // Single eligible price file: pre-select, keep choice visible
  useEffect(() => {
    if (!visible) return
    if (priceFiles.length === 1) {
      const onlyId = priceFiles[0].id
      if (form.getFieldValue('priceFile') !== onlyId) {
        form.setFieldsValue({ priceFile: onlyId })
        onReviewChange?.({
          ...form.getFieldsValue(),
          priceFile: onlyId,
        })
      }
    }
  }, [visible, priceFiles.length])

  useEffect(() => {
    if (!visible) {
      setAdvancedExpanded(false)
      setSending(false)
      onReviewChange?.({})
    }
  }, [visible])

  const buildPayload = values => {
    const deliveryDays = values.deliveryDays || []
    const cutOffTime = values.cutOffTime
      ? moment(values.cutOffTime).format('HH:mm')
      : null

    const addressLines = [
      values.addressLine1?.trim(),
      values.addressLine2?.trim(),
      values.addressCity?.trim(),
      values.addressPostcode?.trim(),
    ]
      .filter(Boolean)
      .join(', ')

    return {
      venue_name: values.venueName,
      contact_name: values.contactName || null,
      contact_phone: values.contactPhone || null,
      email: values.email,
      account_number: values.accountNumber.toUpperCase(),
      price_file_id: values.priceFile,
      advanced: {
        min_order: values.minOrder ? parseFloat(values.minOrder) : null,
        delivery_days: deliveryDays.length > 0 ? deliveryDays : null,
        cut_off: cutOffTime,
        delivery_fee: values.deliveryFee
          ? parseFloat(values.deliveryFee)
          : null,
        address: addressLines || null,
        addressLine1: values.addressLine1 || '',
        addressLine2: values.addressLine2 || '',
        addressCity: values.addressCity || '',
        addressPostCode: values.addressPostcode || '',
      },
    }
  }

  const submitInvite = values =>
    Promise.resolve(onSubmit(buildPayload(values)))
      .catch(() => null)
      .finally(() => {
        setSending(false)
      })

  const handleSubmit = () => {
    if (sending || priceFiles.length === 0) return

    form.validateFields((err, values) => {
      if (err) return
      setSending(true)
      submitInvite(values)
    })
  }

  const handleCancel = () => {
    if (sending) return
    form.resetFields()
    setAdvancedExpanded(false)
    onReviewChange?.({})
    onCancel()
  }

  const goCreatePriceFile = () => {
    handleCancel()
    history.push('/supplier/products')
  }

  const validateAccountNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Account number is required'))
    }
    const pattern = /^[A-Z0-9\-_/\s]+$/
    if (!pattern.test(value.toUpperCase())) {
      return Promise.reject(
        new Error(
          'Account number can only contain letters, numbers, dashes, underscores, slashes, and spaces',
        ),
      )
    }
    return Promise.resolve()
  }

  const reviewVenue =
    reviewValues.venueName || getFieldValue('venueName') || '—'
  const reviewEmail = reviewValues.email || getFieldValue('email') || '—'
  const reviewAccount =
    reviewValues.accountNumber || getFieldValue('accountNumber') || '—'
  const reviewContact =
    reviewValues.contactName || getFieldValue('contactName') || 'Not set'
  const reviewPriceFileName = selectedPriceFile?.name || 'Not selected'

  return (
    <Modal
      title={
        <ModalTitle>
          <UserAddOutlined style={{ marginRight: 8 }} />
          Invite a customer
        </ModalTitle>
      }
      visible={visible}
      onCancel={handleCancel}
      width='95%'
      style={{ top: 24, maxWidth: 600 }}
      destroyOnClose
      maskClosable={!sending}
      keyboard={!sending}
      bodyStyle={{ paddingBottom: 8 }}
      footer={
        <FooterBar>
          <Button onClick={handleCancel} disabled={sending}>
            Cancel
          </Button>
          <Button
            type='primary'
            id='btn-send-invitation'
            onClick={handleSubmit}
            loading={sending}
            disabled={sending || priceFiles.length === 0}
          >
            {sending ? 'Sending…' : 'Send invitation'}
          </Button>
        </FooterBar>
      }
    >
      <ModalContent>
        <Subtitle>
          We&apos;ll create their venue and invite their main contact. They can
          order from you on OMS using your products and prices — ordering is
          free for them.
        </Subtitle>

        <Form layout='vertical'>
          <Section>
            <SectionTitle>Customer</SectionTitle>
            <SectionHint>Who you are inviting</SectionHint>

            <Form.Item
              label={
                <FieldLabel>
                  Venue name <RequiredMark>(required)</RequiredMark>
                </FieldLabel>
              }
              id='fld-venue-name'
              help='Each site should be its own venue. Use "Brand – Location" so you can tell them apart.'
            >
              {getFieldDecorator('venueName', {
                rules: [
                  {
                    required: true,
                    message: 'Please enter venue name',
                  },
                ],
              })(<Input placeholder='Black Bull – Leith' disabled={sending} />)}
            </Form.Item>

            <Form.Item
              label={
                <FieldLabel>
                  Contact name <OptionalMark>(optional)</OptionalMark>
                </FieldLabel>
              }
              id='fld-contact-name'
              help="Used in the greeting of their welcome email. Leave blank if you're not sure."
            >
              {getFieldDecorator('contactName')(
                <Input placeholder='Contact name' disabled={sending} />,
              )}
            </Form.Item>

            <Form.Item
              label={
                <FieldLabel>
                  Contact email <RequiredMark>(required)</RequiredMark>
                </FieldLabel>
              }
              id='fld-email'
              help='This person receives the invitation and will access the venue.'
            >
              {getFieldDecorator('email', {
                rules: [
                  {
                    required: true,
                    message: 'Please enter contact email',
                  },
                  {
                    type: 'email',
                    message: 'Please enter a valid email address',
                  },
                ],
              })(
                <Input
                  type='email'
                  placeholder='chef@venue.com'
                  disabled={sending}
                />,
              )}
            </Form.Item>
          </Section>

          <Section>
            <SectionTitle>Supplier account</SectionTitle>
            <SectionHint>How they link to you in OMS</SectionHint>

            <Form.Item
              label={
                <FieldLabel>
                  Account number <RequiredMark>(required)</RequiredMark>
                </FieldLabel>
              }
              id='fld-account'
              help='Your reference for this customer (often matches the account number in your own system). Used to match orders when you are integrated.'
              required
            >
              {getFieldDecorator('accountNumber', {
                rules: [
                  {
                    required: true,
                    message: 'Account number is required',
                  },
                  {
                    validator: validateAccountNumber,
                  },
                ],
              })(
                <Input
                  placeholder='BB-LEITH-001'
                  disabled={sending}
                  onChange={e => {
                    const value = e.target.value.toUpperCase()
                    form.setFieldsValue({ accountNumber: value })
                  }}
                />,
              )}
            </Form.Item>

            {priceFiles.length === 0 ? (
              <NoPriceFileBox>
                <strong>No price file yet</strong>
                <p>
                  You need a price file with your products (and prices, if you
                  use them) before you can invite a customer.
                </p>
                <Button type='primary' onClick={goCreatePriceFile}>
                  Create a price file
                </Button>
              </NoPriceFileBox>
            ) : (
              <Form.Item
                label={
                  <FieldLabel>
                    Assigned price file <RequiredMark>(required)</RequiredMark>
                  </FieldLabel>
                }
                id='fld-pricefile'
                help={
                  priceFiles.length === 1
                    ? 'Only one price file is available, so it is selected for you. You can change it if you add more later.'
                    : 'Their catalogue and prices come from this file. Choose the file that matches this customer.'
                }
              >
                {getFieldDecorator('priceFile', {
                  initialValue:
                    priceFiles.length === 1 ? priceFiles[0].id : undefined,
                  rules: [
                    {
                      required: true,
                      message: 'Please select a price file',
                    },
                  ],
                })(
                  <Select placeholder='Select price file' disabled={sending}>
                    {priceFiles.map(file => (
                      <Option key={file.id} value={file.id}>
                        {file.name}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            )}

            {hasZeroNamedFile && (
              <NeutralNote>
                Some of your files look like they may have no prices set. That
                means the customer may see products without prices — only use
                that if it matches what you intend. This is not a recommended
                default until you have checked how it appears for customers.
              </NeutralNote>
            )}
          </Section>

          <Section>
            <Collapse
              activeKey={advancedExpanded ? ['advanced'] : []}
              onChange={keys => setAdvancedExpanded(keys.includes('advanced'))}
            >
              <Panel
                header='Optional setup — delivery rules and other settings'
                key='advanced'
              >
                <SectionHint>
                  You can leave these blank and update them later.
                </SectionHint>

                <Form.Item label='Minimum order (£)'>
                  {getFieldDecorator('minOrder')(
                    <Input
                      type='number'
                      min={0}
                      step={0.01}
                      placeholder='0.00'
                      prefix='£'
                      disabled={sending}
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Delivery days'>
                  {getFieldDecorator('deliveryDays', {
                    initialValue: [],
                  })(
                    <DeliveryDaysSelect
                      deliveryDays={form.getFieldValue('deliveryDays') || []}
                      onChange={value => {
                        form.setFieldsValue({ deliveryDays: value })
                      }}
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Order cut-off time'>
                  {getFieldDecorator('cutOffTime')(
                    <TimePicker
                      format='HH:mm'
                      placeholder='17:00'
                      style={{ width: '100%' }}
                      disabled={sending}
                    />,
                  )}
                </Form.Item>

                <Form.Item label={DELIVERY_FEE_LABEL} extra={DELIVERY_FEE_HELP}>
                  {getFieldDecorator('deliveryFee')(
                    <Input
                      type='number'
                      min={0}
                      step={0.01}
                      placeholder='0.00'
                      prefix='£'
                      disabled={sending}
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Contact phone'>
                  {getFieldDecorator('contactPhone')(
                    <Input
                      placeholder='Contact phone (optional)'
                      disabled={sending}
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Address line 1'>
                  {getFieldDecorator('addressLine1')(
                    <Input placeholder='Address line 1' disabled={sending} />,
                  )}
                </Form.Item>

                <Form.Item label='Address line 2'>
                  {getFieldDecorator('addressLine2')(
                    <Input placeholder='Address line 2' disabled={sending} />,
                  )}
                </Form.Item>

                <Form.Item label='Town / city'>
                  {getFieldDecorator('addressCity')(
                    <Input placeholder='Town or city' disabled={sending} />,
                  )}
                </Form.Item>

                <Form.Item label='Postcode'>
                  {getFieldDecorator('addressPostcode')(
                    <Input placeholder='Postcode' disabled={sending} />,
                  )}
                </Form.Item>
              </Panel>
            </Collapse>
          </Section>

          <Section>
            <SectionTitle>Review</SectionTitle>
            <SectionHint>
              Check this before sending. You can still edit the fields above.
            </SectionHint>
            <ReviewBox>
              <ReviewRow>
                <ReviewKey>Venue</ReviewKey>
                <ReviewVal>{reviewVenue}</ReviewVal>
              </ReviewRow>
              <ReviewRow>
                <ReviewKey>Recipient email</ReviewKey>
                <ReviewVal>{reviewEmail}</ReviewVal>
              </ReviewRow>
              <ReviewRow>
                <ReviewKey>Contact name</ReviewKey>
                <ReviewVal>{reviewContact}</ReviewVal>
              </ReviewRow>
              <ReviewRow>
                <ReviewKey>Account number</ReviewKey>
                <ReviewVal>{reviewAccount}</ReviewVal>
              </ReviewRow>
              <ReviewRow>
                <ReviewKey>Price file</ReviewKey>
                <ReviewVal>{reviewPriceFileName}</ReviewVal>
              </ReviewRow>
              <ReviewRow>
                <ReviewKey>Inviting as</ReviewKey>
                <ReviewVal>{supplierDisplayName}</ReviewVal>
              </ReviewRow>
              <WhatNext>
                <strong>What happens next</strong>
                <ul>
                  <li>
                    OMS creates (or links) the venue with your account number
                    and price file.
                  </li>
                  <li>
                    The recipient gets an email to access this venue — existing
                    users sign in; new users create a login.
                  </li>
                  <li>
                    You can follow progress on Customers after the invitation is
                    sent.
                  </li>
                </ul>
              </WhatNext>
            </ReviewBox>
          </Section>
        </Form>
      </ModalContent>
    </Modal>
  )
}

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`

const ModalContent = styled.div`
  padding: 4px 0 8px;
  max-height: min(62vh, 560px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 890px) {
    max-height: min(55vh, 480px);
  }
`

const Subtitle = styled.p`
  margin: 0 0 20px 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
`

const Section = styled.div`
  margin-bottom: 20px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e2e8f0;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
`

const SectionHint = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
`

const FieldLabel = styled.span`
  font-weight: 500;
`

const RequiredMark = styled.span`
  color: #b91c1c;
  font-weight: 500;
  font-size: 12px;
`

const OptionalMark = styled.span`
  color: #64748b;
  font-weight: 400;
  font-size: 12px;
`

const NoPriceFileBox = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  margin-bottom: 8px;

  strong {
    display: block;
    margin-bottom: 6px;
    color: #9a3412;
  }

  p {
    margin: 0 0 12px;
    color: #9a3412;
    font-size: 13px;
    line-height: 1.45;
  }
`

const NeutralNote = styled.p`
  margin: -4px 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  line-height: 1.45;
`

const ReviewBox = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`

const ReviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;

  &:last-of-type {
    border-bottom: none;
  }
`

const ReviewKey = styled.span`
  color: #64748b;
  flex-shrink: 0;
`

const ReviewVal = styled.span`
  color: #0f172a;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
`

const WhatNext = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;

  strong {
    display: block;
    margin-bottom: 6px;
    color: #0f172a;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  li {
    margin-bottom: 4px;
    line-height: 1.4;
  }
`

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 700px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`

const CreatedInviteCustomerModal = Form.create({
  name: 'invite_customer',
  onValuesChange(props, _changed, allValues) {
    props.onReviewChange?.(allValues)
  },
})(InviteCustomerModal)

const WrappedInviteCustomerModal = props => {
  const [reviewValues, setReviewValues] = useState({})
  return (
    <CreatedInviteCustomerModal
      {...props}
      reviewValues={reviewValues}
      onReviewChange={setReviewValues}
    />
  )
}

export default WrappedInviteCustomerModal
